import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { ToggleSwitch } from "../ui/ToggleSwitch";
import { trpc } from "../../lib/trpc";
import "./LineManagementDrawer.css";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

type LineStatus = "running" | "closed" | "faulty" | "maintenance";
type Severity = "low" | "medium" | "high" | "critical";

// badge = header rozeti sınıfı (badge-pill varyantı) · seg = segment "on" rengi sınıfı (CSS'te tanımlı).
const STATUS_META: Record<LineStatus, { label: string; icon: string; badge: string; seg: string }> = {
  running: { label: "Çalışıyor", icon: "play", badge: "b-success", seg: "running" },
  closed: { label: "Kapalı", icon: "square", badge: "", seg: "closed" },
  faulty: { label: "Arızalı", icon: "alertTriangle", badge: "b-danger", seg: "faulty" },
  maintenance: { label: "Bakımda", icon: "wrench", badge: "b-warn", seg: "maintenance" },
};
const STATUS_ORDER: LineStatus[] = ["running", "closed", "faulty", "maintenance"];
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const SEV_OPTS = Object.entries(SEV_LABELS) as [Severity, string][];
const EQTYPE_LABELS: Record<string, string> = { uretim_hatti: "Hat", unite: "Ünite", makina: "Makina" };

interface Props {
  open: boolean;
  centerId: string;
  centerName: string;
  onClose: () => void;
}

interface LocRow { id: string; name: string; type: string; parentId: string | null; }

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Hat Yönetimi — merkezin üretim hatlarının anlık durumunu (Çalışıyor/Kapalı/Arızalı/Bakımda) kaydeder.
 * "Arızalı" seçilince o hattın ünite/makina ağacından ekipman + arıza tipi ile ZORUNLU arıza kaydı girilir.
 * KATI kural: açık arızası olan hat, arıza çözülüp "Kapalı"ya alınana kadar "Çalışıyor"a alınamaz.
 */
export function LineManagementDrawer({ open, centerId, centerName, onClose }: Props) {
  const qc = useQueryClient();
  const linesQ = trpc.lineStatus.list.useQuery({ centerId }, { enabled: open && !!centerId, staleTime: 5_000 });
  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 30_000 });
  const catQ = trpc.faultCatalog.list.useQuery({ centerId, activeOnly: true }, { enabled: open && !!centerId, staleTime: 10_000 });
  // Atanabilir aktif iş emirleri (merkez kuyruğuyla aynı filtre).
  const woQ = trpc.workOrder.list.useQuery(
    { page: 1, pageSize: 100, productionCenterId: centerId, active: true, restrictToActiveOrders: true },
    { enabled: open && !!centerId, staleTime: 10_000 },
  );
  const setMut = trpc.lineStatus.set.useMutation();
  const resolveMut = trpc.faultReport.resolve.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const assignMut = trpc.lineStatus.assignWo.useMutation();

  const [editing, setEditing] = useState<{ lineId: string; status: LineStatus; conditional?: boolean } | null>(null);
  const [note, setNote] = useState("");
  const [faultLoc, setFaultLoc] = useState<string | null>(null);
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [severity, setSeverity] = useState<Severity | "">("");
  const [description, setDescription] = useState("");
  const [prodStopped, setProdStopped] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // İş emri atama editörü + geçiş onayı
  const [assigning, setAssigning] = useState<string | null>(null); // lineId
  const [woSel, setWoSel] = useState<string | null>(null);
  const [switchConfirm, setSwitchConfirm] = useState<{ lineId: string; woId: string | null; currentWoCode: string } | null>(null);

  const condReasonRef = useRef<HTMLTextAreaElement>(null);
  // Şartlı çalıştırma paneli açılınca gerekçe alanına odaklan (kullanıcı doğrudan yazsın).
  useEffect(() => { if (editing?.conditional) { const t = setTimeout(() => condReasonRef.current?.focus(), 60); return () => clearTimeout(t); } }, [editing?.conditional, editing?.lineId]);

  useEffect(() => { if (!open) resetEditor(); /* eslint-disable-next-line */ }, [open]);
  function resetEditor() {
    setEditing(null); setNote(""); setFaultLoc(null); setCatalogId(null); setSeverity(""); setDescription(""); setProdStopped(true); setErr(null);
    setAssigning(null); setWoSel(null); setSwitchConfirm(null);
  }

  const lines = linesQ.data?.lines ?? [];
  const locs = (locsQ.data ?? []) as unknown as LocRow[];
  const catalog = (catQ.data ?? []) as Array<{ id: string; code: string | null; label: string; defaultSeverity: Severity | null }>;
  const catOptions = useMemo(() => catalog.map((x) => ({ value: x.id, label: x.label, desc: x.code ?? undefined, tail: x.defaultSeverity ? SEV_LABELS[x.defaultSeverity] : undefined })), [catalog]);
  const woRows = (woQ.data?.rows ?? []) as Array<{ id: string; code: string; producedName: string; plannedQty: number; producedQty: number }>;
  const woOptions = useMemo(() => woRows.map((w) => ({ value: w.id, label: w.code, desc: w.producedName, tail: `${fmtQty(Math.max(0, w.plannedQty - w.producedQty))} kalan` })), [woRows]);

  function openAssign(lineId: string, currentWoId: string | null) {
    setAssigning(lineId); setWoSel(currentWoId); setErr(null);
  }
  function closeAssign() { setAssigning(null); setWoSel(null); }

  type LineRow = (typeof lines)[number];
  function requestAssign(line: LineRow, woId: string | null) {
    // Meşgul hat (üretimi var) + farklı iş emri → çift onay.
    if (line.hasProduction && line.currentWo && woId !== line.currentWo.id) {
      setSwitchConfirm({ lineId: line.lineId, woId, currentWoCode: line.currentWo.code });
      return;
    }
    void runAssign(line.lineId, woId, false);
  }
  async function runAssign(lineId: string, woId: string | null, confirmSwitch: boolean) {
    setErr(null);
    try {
      await assignMut.mutateAsync({ lineId, woId, confirmSwitch });
      await qc.invalidateQueries();
      closeAssign(); setSwitchConfirm(null);
    } catch (e) {
      const msg = (e as Error)?.message ?? "";
      // İstemci sezgisi bayat veri yüzünden kaçırmış olabilir → backend onay istedi: ham hata yerine dialog.
      if (msg.includes("SWITCH_CONFIRM") && !confirmSwitch) {
        const code = msg.match(/"([^"]+)"/)?.[1] ?? lines.find((l) => l.lineId === lineId)?.currentWo?.code ?? "";
        setSwitchConfirm({ lineId, woId, currentWoCode: code });
      } else {
        setErr(msg); setSwitchConfirm(null);
      }
    }
  }

  function pickStatus(lineId: string, status: LineStatus) {
    setErr(null);
    setEditing({ lineId, status });
    setNote(""); setFaultLoc(null); setCatalogId(null); setSeverity(""); setDescription(""); setProdStopped(true);
  }
  // Şartlı çalıştırma: arızaya rağmen (gerekçeyle) hattı çalıştırma editörü.
  function startConditional(lineId: string) {
    setErr(null);
    setEditing({ lineId, status: "running", conditional: true });
    setNote(""); setFaultLoc(null); setCatalogId(null); setSeverity(""); setDescription(""); setProdStopped(true);
  }
  function pickCatalog(v: string | null) {
    setCatalogId(v);
    const c = catalog.find((x) => x.id === v);
    if (c?.defaultSeverity && !severity) setSeverity(c.defaultSeverity);
  }

  async function apply() {
    if (!editing) return;
    setErr(null);
    try {
      if (editing.status === "faulty") {
        if (!faultLoc) { setErr("Arıza için ekipman seçin (hat/ünite/makina)."); return; }
        if (!catalogId) { setErr("Arıza tipi seçin (liste boşsa Lokasyonlar > Arıza Kataloğu'ndan tanımlayın)."); return; }
        await setMut.mutateAsync({
          lineId: editing.lineId, status: "faulty", note: note.trim() || null,
          fault: { locationId: faultLoc, catalogId, severity: severity || null, description: description.trim() || null, productionStopped: prodStopped },
        });
      } else if (editing.conditional) {
        if (!note.trim()) { setErr("Şartlı çalıştırma için gerekçe zorunludur."); return; }
        await setMut.mutateAsync({ lineId: editing.lineId, status: "running", conditionalRun: true, overrideReason: note.trim() });
      } else {
        await setMut.mutateAsync({ lineId: editing.lineId, status: editing.status, note: note.trim() || null });
      }
      await qc.invalidateQueries();
      resetEditor();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xxl"
      title="Hat Yönetimi"
      subtitle={centerName ? `Merkez: ${centerName}` : undefined}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {linesQ.isLoading ? (
        <div className="lm-empty">Yükleniyor…</div>
      ) : lines.length === 0 ? (
        <div className="lm-empty">Bu merkezde üretim hattı tanımlı değil — Lokasyonlar sayfasından hat ekleyebilirsiniz.</div>
      ) : (
        <div className="lm-list">
          {lines.map((l) => {
            const cur = l.current?.status as LineStatus | undefined;
            const meta = cur ? STATUS_META[cur] : null;
            const hasOpenFault = l.openFaults.length > 0;
            const isEditing = editing?.lineId === l.lineId;
            // KATI kural: hat arızalıyken veya açık arıza varken çalıştırılamaz (resolve + Kapalı gerekli).
            const runBlocked = hasOpenFault || cur === "faulty";
            return (
              <div key={l.lineId} className="d-card">
                {/* Başlık: hat adı/kodu + güncel durum rozeti */}
                <div className="d-card-head">
                  <div className="left">
                    <span className="t">{l.name}</span>
                    <span className="s">{l.code}</span>
                  </div>
                  {meta ? (
                    l.current?.conditionalRun && cur === "running" ? (
                      <span className="badge-pill b-warn" title={l.current.overrideReason ? `Şartlı çalışıyor — gerekçe: ${l.current.overrideReason}` : "Arızaya rağmen şartlı çalışıyor (arıza açık)"}>
                        <Icon name="alertTriangle" size={12} />Şartlı Çalışıyor
                      </span>
                    ) : hasOpenFault && cur !== "faulty" && cur !== "maintenance" ? (
                      // Ekipmanda aktif arıza/bakım var ama hat kaydı 'çalışıyor'/'kapalı' → çelişki: arızalı göster.
                      <span className="badge-pill b-danger" title="Ekipmanda aktif arıza/bakım var — müdahale gerekiyor (hat kaydı güncel değil)">
                        <Icon name="alertTriangle" size={12} />Arızalı
                      </span>
                    ) : (
                      <span className={`badge-pill ${meta.badge}`}><Icon name={meta.icon} size={12} />{meta.label}</span>
                    )
                  ) : hasOpenFault ? (
                    <span className="badge-pill b-danger" title="Ekipmanda aktif arıza/bakım var"><Icon name="alertTriangle" size={12} />Arızalı</span>
                  ) : (
                    <span className="lm-muted">Durum girilmemiş</span>
                  )}
                </div>

                <div className="d-card-body tight lm-body">
                  {/* İş emri ataması */}
                  <div className="lm-row">
                    <span className="lm-label">İş Emri</span>
                    {l.currentWo ? (
                      <span className="lm-wo">
                        <span className="code">{l.currentWo.code}</span>
                        <span className="name">{l.currentWo.producedName}</span>
                        <span className="qty">{fmtQty(l.currentWo.producedQty)}/{fmtQty(l.currentWo.plannedQty)}</span>
                      </span>
                    ) : (
                      <span className="lm-muted">Boşta</span>
                    )}
                    <span className="lm-spacer" />
                    <button type="button" className="btn-md secondary lm-btn-sm" onClick={() => openAssign(l.lineId, l.currentWo?.id ?? null)}>
                      {l.currentWo ? "Değiştir" : "İş Emri Ata"}
                    </button>
                  </div>
                  {assigning === l.lineId ? (
                    <div className="lm-panel">
                      {woRows.length === 0 ? (
                        <div className="lm-hint">Bu merkezde atanabilir aktif iş emri yok.</div>
                      ) : (
                        <SearchSelect value={woSel} options={woOptions} onChange={setWoSel} placeholder="İş emri ara/seç…" searchPlaceholder="Ara…" icon="fileText" />
                      )}
                      <div className="lm-actions">
                        <button type="button" className="btn-md ghost" onClick={closeAssign} disabled={assignMut.isPending}>Vazgeç</button>
                        {l.currentWo ? <button type="button" className="btn-md secondary" onClick={() => requestAssign(l, null)} disabled={assignMut.isPending}>Boşta Bırak</button> : null}
                        <button type="button" className="btn-md primary" onClick={() => requestAssign(l, woSel)} disabled={!woSel || woSel === l.currentWo?.id || assignMut.isPending}>
                          <Icon name="check" size={14} />Ata
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* Açık arızalar */}
                  {hasOpenFault ? (
                    <div className="lm-fault">
                      <div className="lm-fault-title">Açık Arıza — çalıştırmak için: arızayı çöz → "Kapalı" → "Çalışıyor"</div>
                      {l.openFaults.map((f) => (
                        <div key={f.id} className="lm-fault-item">
                          <span className="name">{f.faultLabel}</span>
                          {f.equipment ? <span className="sub">· {f.equipmentType ? `${EQTYPE_LABELS[f.equipmentType] ?? ""} ` : ""}{f.equipment}</span> : null}
                          {f.severity ? <span className="sub">· {SEV_LABELS[f.severity as Severity]}</span> : null}
                          <span className="lm-spacer" />
                          {f.inMaintenance ? (
                            // Bakım emrine dönüştürüldü: buradan çözülemez (katı kural) — bakım emri tamamlanınca kalkar.
                            <span className="badge-pill b-info" title="Bakım emrine dönüştürüldü — hat, bağlı bakım emri tamamlanınca çalıştırılabilir">
                              <Icon name="wrench" size={11} />Bakımda
                            </span>
                          ) : (
                            <button type="button" className="iact success" aria-label="Çözüldü olarak işaretle" title="Arızayı çöz" onClick={() => resolveMut.mutate({ id: f.id })} disabled={resolveMut.isPending}>
                              <Icon name="check" size={15} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {!hasOpenFault && cur === "faulty" ? (
                    <div className="lm-hint">Arıza çözüldü. Çalıştırmak için önce hattı "Kapalı"ya alın, sonra "Çalışıyor" seçin.</div>
                  ) : null}

                  {/* Durum segmenti */}
                  <div className="lm-row">
                    <span className="lm-label">Durum</span>
                    <div className="lm-seg" role="group" aria-label="Hat durumu">
                      {STATUS_ORDER.map((s) => {
                        const sm = STATUS_META[s];
                        const on = isEditing ? editing?.status === s : cur === s;
                        // Arızalı hatta 'Çalışıyor' tıklanınca disabled değil → doğrudan Şartlı Çalıştırma
                        // panelini açar (gerekçe iste). Kullanıcının "çalıştır" tıklaması boşa gitmez.
                        const needsOverride = s === "running" && runBlocked;
                        return (
                          <button
                            key={s}
                            type="button"
                            className={on ? `on ${sm.seg}` : ""}
                            onClick={() => (needsOverride ? startConditional(l.lineId) : pickStatus(l.lineId, s))}
                            title={needsOverride ? "Hat arızalı — 'Çalışıyor' için Şartlı Çalıştırma gerekir. Tıklayınca gerekçe alanı açılır (arıza açık kalır)." : undefined}
                          >
                            <Icon name={sm.icon} size={13} />{sm.label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Arızalıysa: Şartlı Çalıştır — Durum grubuyla AYNI satırda, sağa yaslı (arıza açık kalır) */}
                    {runBlocked && !(isEditing && editing?.conditional) ? (
                      <>
                        <span className="lm-spacer" />
                        <button type="button" className="btn-md warn lm-btn-sm" onClick={() => startConditional(l.lineId)} title="Arızaya rağmen gerekçeyle çalıştır — arıza açık kalır">
                          <Icon name="alertTriangle" size={13} />Şartlı Çalıştır
                        </button>
                      </>
                    ) : null}
                  </div>

                  {/* Durum editörü (seçilen duruma göre not / zorunlu arıza formu) */}
                  {isEditing ? (
                    <div className="lm-panel">
                      {editing?.status === "faulty" ? (
                        <>
                          <div className="lm-panel-title danger">Arıza Kaydı (zorunlu)</div>
                          <div className="form-field">
                            <label className="lbl">Ekipman (arızanın yeri) <span className="req">*</span></label>
                            <LineEquipTree rootId={l.lineId} lineName={l.name} locations={locs} selected={faultLoc} onSelect={setFaultLoc} />
                          </div>
                          <div className="form-field">
                            <label className="lbl">Arıza Tipi <span className="req">*</span></label>
                            {catalog.length === 0 ? (
                              <div className="lm-hint">Bu merkez için arıza tipi tanımlı değil. Lokasyonlar → merkez → <b>Arıza Kataloğu</b>'ndan ekleyin.</div>
                            ) : (
                              <SearchSelect value={catalogId} options={catOptions} onChange={pickCatalog} placeholder="Arıza tipi ara/seç…" searchPlaceholder="Ara…" icon="alertTriangle" />
                            )}
                          </div>
                          <div className="form-row">
                            <div className="form-field">
                              <label className="lbl" htmlFor="lm-sev">Önem</label>
                              <select id="lm-sev" className="form-select" value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
                                <option value="">—</option>
                                {SEV_OPTS.map(([v, l2]) => <option key={v} value={v}>{l2}</option>)}
                              </select>
                            </div>
                            <div className="form-field">
                              <label className="lbl">&nbsp;</label>
                              <div className="lm-stop-check">
                                <ToggleSwitch checked={prodStopped} onChange={setProdStopped} label="Üretim durdu" />
                              </div>
                            </div>
                          </div>
                          <div className="form-field">
                            <label className="lbl" htmlFor="lm-desc">Açıklama</label>
                            <textarea id="lm-desc" className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={1000} placeholder="Arıza detayı…" />
                          </div>
                          <div className="form-field">
                            <label className="lbl" htmlFor="lm-fnote">Durum notu (opsiyonel)</label>
                            <input id="lm-fnote" className="form-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Hat durum notu…" />
                          </div>
                        </>
                      ) : editing?.conditional ? (
                        <>
                          <div className="lm-panel-title danger">Şartlı Çalıştırma (arızaya rağmen)</div>
                          <div className="form-field">
                            <label className="lbl" htmlFor="lm-cond">Gerekçe <span className="req">*</span></label>
                            <textarea ref={condReasonRef} id="lm-cond" className="form-textarea" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="Neden arızaya rağmen çalıştırılıyor?" />
                            <span className="help">Gerekçe zorunlu. Hat <b>"Şartlı Çalışıyor"</b> olarak işaretlenir; arıza kaydı AÇIK kalır (audit'e yazılır).</span>
                          </div>
                        </>
                      ) : (
                        <div className="form-field">
                          <label className="lbl" htmlFor="lm-note">Not (opsiyonel)</label>
                          <input id="lm-note" className="form-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={`${STATUS_META[editing!.status].label} — açıklama…`} />
                        </div>
                      )}
                      <div className="lm-actions">
                        <button type="button" className="btn-md ghost" onClick={resetEditor} disabled={setMut.isPending}>Vazgeç</button>
                        <button type="button" className={`btn-md ${editing?.status === "faulty" ? "danger" : editing?.conditional ? "warn" : "primary"}`} onClick={apply} disabled={setMut.isPending}>
                          <Icon name={editing?.conditional ? "alertTriangle" : STATUS_META[editing!.status].icon} size={14} />
                          {setMut.isPending ? "Kaydediliyor…" : editing?.status === "faulty" ? "Arızalı Kaydet" : editing?.conditional ? "Onayla ve Çalıştır" : `${STATUS_META[editing!.status].label} Yap`}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* Son değişiklik meta */}
                  {l.current ? (
                    <div className="lm-meta">Son değişiklik: {fmtDateTime(l.current.startedAt)}{l.current.changedBy ? ` · ${l.current.changedBy}` : ""}{l.current.note ? ` · ${l.current.note}` : ""}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!switchConfirm}
        title="İş emrini değiştir?"
        message={
          <>
            Bu hatta <b>{switchConfirm?.currentWoCode}</b> iş emri çalışıyor (üretimi var).{" "}
            {switchConfirm?.woId ? <>Hattın işini değiştirmek üzeresiniz.</> : <>Hattı boşta bırakmak üzeresiniz.</>} Devam edilsin mi?
          </>
        }
        tone="danger"
        confirmLabel="Onayla, Değiştir"
        cancelLabel="Vazgeç"
        busy={assignMut.isPending}
        onConfirm={() => switchConfirm && runAssign(switchConfirm.lineId, switchConfirm.woId, true)}
        onCancel={() => setSwitchConfirm(null)}
      />
    </Drawer>
  );
}

/** Hat alt-ağacı (yalnız bu hattın ünite/makinaları) + "Hat geneli" seçeneği; arama + varsayılan-kapalı. */
function LineEquipTree({ rootId, lineName, locations, selected, onSelect }: {
  rootId: string; lineName: string; locations: LocRow[]; selected: string | null; onSelect: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const nodes = useMemo(() => {
    const byParent = new Map<string, LocRow[]>();
    locations.forEach((r) => { if (r.parentId) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); } });
    const out: Array<{ id: string; name: string; type: string; depth: number; parentId: string; hasChildren: boolean }> = [];
    const walk = (pid: string, depth: number) => {
      const kids = (byParent.get(pid) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
      for (const k of kids) {
        if (k.type === "unite" || k.type === "makina") {
          const kidHas = (byParent.get(k.id) ?? []).some((c) => c.type === "unite" || c.type === "makina");
          out.push({ id: k.id, name: k.name, type: k.type, depth, parentId: pid, hasChildren: kidHas });
          walk(k.id, depth + 1);
        }
      }
    };
    walk(rootId, 0);
    return out;
  }, [locations, rootId]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const inTree = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);
  const norm = (s: string) => s.toLocaleLowerCase("tr-TR");
  const search = q.trim();

  const visible = useMemo(() => {
    if (search) {
      const needle = norm(search);
      const show = new Set<string>();
      for (const n of nodes) {
        if (norm(n.name).includes(needle)) {
          show.add(n.id);
          let p: string | undefined = n.parentId;
          while (p && p !== rootId) { show.add(p); p = byId.get(p)?.parentId; }
        }
      }
      return nodes.filter((n) => show.has(n.id));
    }
    return nodes.filter((n) => {
      let p: string | undefined = n.parentId;
      while (p && p !== rootId) { if (!expanded.has(p)) return false; p = byId.get(p)?.parentId; }
      return true;
    });
  }, [nodes, byId, search, expanded, rootId]);

  function toggle(id: string) { setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }
  function pick(id: string) {
    onSelect(id);
    setExpanded((s) => { const n = new Set(s); let p = byId.get(id)?.parentId; while (p && p !== rootId) { n.add(p); p = byId.get(p)?.parentId; } return n; });
  }

  return (
    <>
      <input className="form-input" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ekipman ara… (ünite/makina adı)" style={{ marginBottom: 6 }} />
      <div style={{ border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", maxHeight: 170, overflowY: "auto", padding: 4 }}>
        {/* Hat geneli = hattın kendisi (locationId = rootId) */}
        <EqRow name={`${lineName} (hat geneli)`} type={null} depth={0} selected={selected === rootId} onClick={() => onSelect(rootId)} />
        {nodes.length === 0 ? (
          <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>Bu hatta ünite/makina tanımlı değil — hat geneli seçilebilir.</div>
        ) : search && visible.length === 0 ? (
          <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>"{search}" ile eşleşen ekipman yok.</div>
        ) : visible.map((n) => (
          <EqRow
            key={n.id}
            name={n.name}
            type={n.type}
            depth={n.depth + 1}
            selected={selected === n.id}
            onClick={() => pick(n.id)}
            hasChildren={n.hasChildren}
            expanded={!!search || expanded.has(n.id)}
            onToggle={search ? undefined : () => toggle(n.id)}
          />
        ))}
      </div>
    </>
  );
}

function EqRow({ name, type, depth, selected, onClick, hasChildren = false, expanded = false, onToggle }: {
  name: string; type: string | null; depth: number; selected: boolean; onClick: () => void;
  hasChildren?: boolean; expanded?: boolean; onToggle?: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, paddingLeft: depth * 16 }}>
      {hasChildren && onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? "Daralt" : "Genişlet"}
          aria-expanded={expanded}
          style={{ width: 18, height: 18, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", borderRadius: 4, cursor: "pointer", color: "var(--neutral-500)", padding: 0, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 100ms" }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </button>
      ) : (
        <span style={{ width: 18, flexShrink: 0 }} />
      )}
      <button
        type="button"
        onClick={onClick}
        style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, textAlign: "left", padding: "5px 8px", border: "none", borderRadius: 6, cursor: "pointer", background: selected ? "var(--primary-subtle)" : "transparent", color: "var(--neutral-800)", fontSize: 13 }}
      >
        <span style={{ width: 12, display: "inline-flex", justifyContent: "center", color: selected ? "var(--primary)" : "var(--neutral-300)" }}>
          {selected ? <Icon name="check" size={13} /> : <span style={{ width: 7, height: 7, borderRadius: 999, border: "1.5px solid currentColor", display: "inline-block" }} />}
        </span>
        {type ? <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", padding: "1px 5px", borderRadius: 4, color: "var(--neutral-600)", background: "var(--neutral-100)", whiteSpace: "nowrap" }}>{EQTYPE_LABELS[type] ?? type}</span> : null}
        <span style={{ fontWeight: selected ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </button>
    </div>
  );
}
